export type ValidationRepairCopyTab = 'recommended' | 'surgery' | 'history';

export const VALIDATION_REPAIR_COPY = {
  detailRepairActiveLabel: 'Detail repair active',
  repairEditorLabel: 'Repair editor',
  repairStateLabel: 'Repair state',
  repairTargetLabel: 'Repair target',
  afterProposedRepairLabel: 'After proposed repair',
  reviewAndApplyRepairLabel: 'Review and apply repair',
  cancelRepairLabel: 'Cancel repair',
  revertLastRepairLabel: 'Revert last repair',
  reviewRepairLabel: 'Review repair',
  confirmRepairLabel: 'Confirm repair',
  incompleteRepairFormMessage: 'Complete the repair form before applying the change.',
  repairFailedMessage: 'Repair failed',
  failedToApplyRepairMessage: 'Failed to apply repair',
  sessionRepairHistoryMessage: 'Session repair history is recorded for this issue family.',
  noWorkflowChangesMessage: 'No workflow-level changes pending.',
  workflowPanel: {
    exitDetailRepair: 'Exit',
    validateStagedRepair: 'Validate staged repair',
    validateCurrentInstance: 'Validate current issue',
    runFocused: 'Run focused validation',
    busyValidating: 'Running validation…',
    busyApplyingRepair: 'Applying repair…',
  },
  surgeryForm: {
    detailRepair: 'Detail repair',
    scopeLabel: 'Scope',
    targetItemLabel: 'Target item',
    fieldLabel: 'Field or entry',
    currentValueLabel: 'Current value',
    expectedValueLabel: 'Expected value',
    sourcePathLabel: 'Source path',
    issueTypeLabel: 'Issue type',
    manualRepairRecommendedAlert:
      'Recommended path: manual repair. Revalidation comes after you stage a specific repair.',
    resolvedRepairOptions: 'Resolved repair options',
    directUndoableLabel: 'Direct and undoable',
    guidedRepairLabel: 'Guided repair',
    noDirectOverrideMessage:
      'No direct parameter-level repair override is available for this issue type. Use the guided quick-fix path below, then validate the current instance from this session.',
    guidedQuickFixPath: 'Guided quick-fix path',
    openInModel: 'Open in model',
    scopeControl: 'Scope control',
    scopeControlHint:
      'Not used for guided-only repairs. Direct value editing and explicit scope selection apply when a direct repair action is available.',
    beforeAfterPreview: 'Before-and-after preview',
    beforeAfterPreviewHint:
      'A before-and-after preview appears when you use a direct parameter repair. For guided issues, use the action above and run validation again.',
    validationPreview: 'Validation preview',
    validationPreviewHint:
      'Use Validate current issue after a guided step. Full repair validation checks appear for direct repairs.',
    repairActionLabel: 'Repair action',
    proposedValueLabel: 'Proposed value',
    enterCorrectedValuePlaceholder: 'Enter corrected value',
    beforeLabel: 'Before',
    pendingInput: 'Pending input',
    expectedOutcomeLabel: 'Expected outcome',
    predictedValidationResultLabel: 'Predicted validation result',
    autoAdvanceCheckboxLabel: 'Auto-advance to next unresolved instance after validation',
    autoAdvanceAriaLabel: 'Auto-advance to next unresolved instance after validation',
    applying: 'Applying…',
    undoLastQuickFix: 'Undo last quick fix',
    resetChanges: 'Reset changes',
    highlightAllFailingPoints: 'Highlight all failing points',
    openConditionStudio: 'Open Condition Studio',
    copyIssueList: 'Copy issue list',
    riskLevelLabel: 'Risk level',
  },
  recommendedPanel: {
    issuePath: 'Issue path',
    recommendedNextStep: 'Recommended next step',
    manualRepairPath: 'Recommended path: manual repair',
    manualRepairReason:
      'This issue does not map cleanly to an automatic fix surface and needs exact review of the failing point.',
    startDetailRepair: 'Start detail repair',
    openQuickFill: 'Open Quick Fill for safe slot swaps',
    openDetailRepair: 'Open detail repair',
    runFocusedValidation: 'Run focused validation',
    openInModel: 'Open in model',
    highlightAllFailingPoints: 'Highlight all failing points',
    openConditionStudio: 'Open Condition Studio',
    copyIssueList: 'Copy issue list',
    followUpActionsLabel: 'Follow-up actions',
    validateCurrentDescription: 'Re-check the selected repair scope against the current rule.',
    runFocusedValidationDescription:
      'Run only the validation check that owns this failing point.',
    openInModelDescription: 'Isolate this failing item in the model view.',
    openInModelDisabledReason: 'No model elements are linked to this failing item.',
    highlightAllDescription: 'Show every affected element for this issue family.',
    highlightAllDisabledReason:
      'No affected model elements are recorded for this issue family.',
    openDetailRepairDescription: 'Stage an exact manual repair with a live before/after preview.',
    copyIssueListDescription: 'Copy a shareable text summary of this issue family.',
    opensTargetWorkspace: (target: string) => `Opens ${target}`,
    opensConditionStudio: 'Opens Condition Studio',
    busyValidating: 'Running validation…',
  },
  evidencePanel: {
    repairSessionActive: (itemName: string) =>
      `Repair session active for ${itemName}. Focus on the exact target, stage the change, review the live preview, then validate before moving on.`,
    whyItFailedEndTypeMismatch: (
      reason: string,
      fieldLabel: string,
      currentType: string,
      currentParam: string,
      currentValue: string,
      connectedType: string,
      connectedParam: string,
      connectedValue: string
    ) =>
      `${reason} The connected pair does not share a matching non-empty ${fieldLabel} assignment. ${currentType} resolves ${currentParam} to ${currentValue}, while ${connectedType} resolves ${connectedParam} to ${connectedValue}.`,
    whyItFailedNoSystemBinding: (reason: string) =>
      `${reason} The selected component does not resolve to an active MEP piping system through either its curve-level system binding or its end connectors, so export grouping cannot place it in a valid piping system.`,
    whyItFailedGeometryOverlap: (reason: string) =>
      `${reason} The selected element pair occupies the same physical space without a recognized connection, which usually indicates an unintended clash, duplicate placement, or branch condition that was not exempted by the overlap rules.`,
    whyItFailedDefault:
      'Neither Quick Fill nor the mapped Revit parameter provides a valid #PCF_OBJECT_TYPE value, so PipeEX cannot determine the OBJECT-TYPE for this target.',
    nextStepObjectType: 'Assign a valid OBJECT-TYPE value. This parameter is required for PCF export.',
    whyItFailed: 'Why it failed',
    nextStepPrefix: 'Next step:',
    impact: 'Impact',
    impactedReferences: (count: number) =>
      `${count} affected reference${count === 1 ? '' : 's'}`,
    noAffectedElements: 'No explicit affected elements listed',
    exactFailure: 'Exact failure',
    currentValue: 'Current value',
    expectedValue: 'Expected value',
    requirement: 'Requirement',
    severity: 'Severity',
    repairSurface: 'Repair surface',
    repairContext: 'Repair context',
    editableTarget: 'Editable target',
    repairField: 'Repair field',
    currentState: 'Current state',
    proposedState: 'Proposed state',
    sourcePath: 'Source path',
    predictedResult: 'Predicted result',
    awaitingStagedRepair: 'Awaiting staged repair',
    evidence: 'Evidence',
    repairEvidence: 'Repair evidence',
    evaluationChain: 'Evaluation chain',
    failingPoint: 'Failing point',
    scope: 'Scope',
    target: 'Target',
    field: 'Field',
    source: 'Source',
    status: 'Status',
    editableAffectedTargetContext: 'Editable and affected target context',
    affectedTargetSummary: (
      reason: string,
      sourceParameter: string,
      sourceLocation: string,
      targetCount: number,
      scopeLabel: string
    ) =>
      `${reason} The failing point is ${sourceParameter} at ${sourceLocation.toLowerCase()}, and this repair currently affects ${targetCount} target${targetCount === 1 ? '' : 's'} in ${scopeLabel.toLowerCase()}.`,
    affectedItemsLabel: 'Affected items',
    hideRawIssueDetails: 'Hide raw issue details',
    viewRawIssueDetails: 'Raw issue details',
  },
  historyPanel: {
    noHistory: 'No repair history yet for this issue family.',
    targetsSummary: (count: number) => `${count} target${count === 1 ? '' : 's'}`,
    undoErrorLabel: 'Undo error',
    undoQuickFix: 'Undo quick fix',
  },
  surgeryDialog: {
    reviewRepairFallbackTitle: 'Review repair',
    reviewStagedRepairAlert:
      'Review the staged repair first. Nothing changes until you confirm.',
    validationRepairTitle: 'Validation repair',
    validationRepairQueued: 'Validation repair queued for Revit.',
    targetItemLabel: 'Target item',
    unknownTarget: 'Unknown target',
    scopeLabel: 'Scope',
    currentValueLabel: 'Current value',
    emptyValue: 'empty',
    proposedValueLabel: 'Proposed value',
    expectedOutcomeLabel: 'Expected outcome',
    reviewValidationResult: 'Review validation result',
    undoAvailable: 'Undo will be available after apply.',
    undoLimited:
      'Undo may be limited for this repair because the runtime does not expose the full previous value set.',
    cancel: 'Cancel',
    applying: 'Applying…',
  },
  detailHeader: {
    backAriaLabel: 'Back',
    pageGuideAriaLabel: 'Page guide',
    previousAriaLabel: 'Previous',
    nextAriaLabel: 'Next',
    instanceCounter: (current: number, total: number) => `Instance ${current} of ${total}`,
    repairingFieldSuffix: (fieldLabel: string) => ` · Repairing ${fieldLabel}`,
    repairModePill: 'Repair mode',
    runFocused: 'Run focused validation',
    busyValidating: 'Running validation…',
    moreActionsAriaLabel: 'More repair actions',
    openInModel: 'Open in model',
    thisFailingItemOnly: 'This failing item only',
    highlightAllFailingPoints: 'Highlight all failing points',
    allElementsForIssue: 'All elements for this issue',
    openConditionStudio: 'Open Condition Studio',
    reviewScopeExceptions: 'Review scope exceptions',
    copyIssueList: 'Copy issue list',
    copyToClipboard: 'Copy to clipboard',
  },
  navigator: {
    unknown: 'Unknown',
    unknownTarget: 'Unknown target',
    validationField: 'Validation field',
    unresolved: 'Unresolved',
    pointsCount: (count: number) => `${count} ${count === 1 ? 'point' : 'points'}`,
    pendingSummary: (pendingCount: number, totalCount: number) =>
      pendingCount === totalCount
        ? `${totalCount} ${totalCount === 1 ? 'point' : 'points'}`
        : `${pendingCount} open / ${totalCount}`,
    showGroup: 'Show',
    hideGroup: 'Hide',
    groupAriaLabel: (action: 'Show' | 'Hide', title: string) => `${action} ${title} group`,
    skipSimilarGroupAriaLabel: (title: string) => `Skip similar ${title} group`,
    skipSimilarGroupTitle: 'Skip similar group',
    idPrefix: 'ID',
    currentPrefix: 'Current:',
    ref: '1 ref',
    refs: (count: number) => `${count} refs`,
    failingPointsTitle: 'Failing points',
    filterPlaceholder: 'Filter failing points',
    filterAriaLabel: 'Filter failing validation points',
  },
  snackbars: {
    stageOrCompleteRepairBeforeValidate: 'Stage or complete a repair before validating this issue.',
    noUndoableQuickFix: 'No undoable quick fix is available for this item.',
    quickFixUndone: 'Quick fix was undone.',
    failedToUndoQuickFix: 'Failed to undo quick fix',
    noModelElementsToHighlight: 'No model elements are available to highlight for this issue.',
    reconnectPairUnavailable: 'Reconnect pair is not available for this issue.',
    reconnectAllUnavailable: 'Reconnect all is not available for this issue.',
  },
  validation: {
    repairResolved: 'Repair resolved',
    scopedRevalidation: 'Scoped revalidation',
    resolvedAfterValidation: 'Resolved after validation',
    validatedAgainstCurrentRule: (itemName: string) =>
      `Validated ${itemName} against the current rule.`,
    validatedAndResolved: (scopeLabel: string, itemName: string) =>
      `Validated ${scopeLabel} and resolved ${itemName}`,
    revalidatedFor: (scopeLabel: string, itemName: string) =>
      `Revalidated ${scopeLabel} for ${itemName}`,
  },
  page: {
    backToRepairConsole: 'Back to repair console',
    nextUnresolved: 'Next unresolved',
    outcomeScopePrefix: 'Scope:',
    repairDetailWorkspaceTour: 'Repair detail workspace tour',
    repairWorkspaceTitle: 'Repair workspace',
    loadingRepairWorkspaceTitle: 'Loading repair workspace…',
    loadingRepairWorkspaceMessage: 'Preparing the repair editor',
    loadingRepairWorkspaceDetail:
      'Failing points, evidence, and repair actions for the selected issue will appear here in a moment.',
    loadingRepairWorkspacePhase: 'Loading',
    loadingRepairWorkspaceSteps: [
      'Load the issue family',
      'Prepare failing points',
      'Open repair actions',
    ],
  },
  progress: {
    undoQuickFixTitle: 'Undo quick fix',
    undoQuickFixQueued: 'Quick fix undo queued for Revit.',
  },
  provenSwapSummary: (count: number) =>
    `${count} proven swap${count === 1 ? '' : 's'} can be staged for review.`,
} as const;

export function getRepairTabLabel(tab: ValidationRepairCopyTab): string {
  switch (tab) {
    case 'surgery':
      return 'Detail repair';
    case 'history':
      return 'History';
    default:
      return 'Recommended fix';
  }
}

export function getRepairModeActionLabel(isDetailRepairActive: boolean, entryLabel: string): string {
  return isDetailRepairActive ? VALIDATION_REPAIR_COPY.detailRepairActiveLabel : entryLabel;
}

export function getRepairRollbackLabel(itemName: string): string {
  return `${itemName} repair rollback`;
}

export function getRepairHistoryDescription(itemName: string): string {
  return `Applied manual repair to ${itemName}`;
}

export function getRepairStagedSummary(itemName: string): string {
  return `Repair staged for ${itemName}. Review the preview, then validate this repair session to confirm the rule clears.`;
}

export function getRepairStagedToast({
  fixedCount,
  totalCount,
  undoAvailable,
}: {
  fixedCount: number;
  totalCount: number;
  undoAvailable: boolean;
}): string {
  return undoAvailable
    ? `Repair staged for ${fixedCount} of ${totalCount} targets. Undo is available.`
    : 'Repair staged. Validate the staged repair when ready.';
}

export function getRepairValidatedSummary(itemName: string): string {
  return `${itemName} validated successfully after the staged repair.`;
}
